/**
 * Region Detection Utility
 * Detects user's region using browser-based methods (no Markets required)
 *
 * Methods used:
 * 1. Browser language preference (navigator.language)
 * 2. Timezone detection (Intl.DateTimeFormat)
 * 3. Optional: IP-based geolocation (if API is configured)
 */

class RegionDetection {
  constructor() {
    this.detectedRegion = null;
    this.detectionMethods = {
      language: null,
      timezone: null,
      ip: null
    };
  }

  /**
   * Detect region using browser language
   * Returns ISO 639-1 language code (e.g., 'en', 'de', 'es')
   */
  detectByLanguage() {
    if (typeof navigator === 'undefined') return null;

    // Get primary language
    const language = navigator.language || navigator.userLanguage || 'en';
    const langCode = language.split('-')[0].toLowerCase();

    // Get all preferred languages
    const languages = navigator.languages || [language];

    this.detectionMethods.language = {
      primary: langCode,
      all: languages.map(lang => lang.split('-')[0].toLowerCase()),
      full: language
    };

    return langCode;
  }

  /**
   * Detect region using timezone
   * Returns timezone identifier (e.g., 'Europe/Berlin', 'America/New_York')
   */
  detectByTimezone() {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      this.detectionMethods.timezone = timezone;

      // Extract region hint from timezone
      const regionHint = this.getRegionFromTimezone(timezone);

      return {
        timezone: timezone,
        regionHint: regionHint
      };
    } catch (e) {
      console.warn('Timezone detection failed:', e);
      return null;
    }
  }

  /**
   * Map timezone to likely country/region
   */
  getRegionFromTimezone(timezone) {
    const timezoneMap = {
      // Europe
      'Europe/Berlin': 'DE',
      'Europe/Paris': 'FR',
      'Europe/London': 'GB',
      'Europe/Madrid': 'ES',
      'Europe/Rome': 'IT',
      'Europe/Amsterdam': 'NL',
      'Europe/Brussels': 'BE',
      'Europe/Vienna': 'AT',
      'Europe/Zurich': 'CH',
      'Europe/Warsaw': 'PL',
      'Europe/Stockholm': 'SE',
      'Europe/Oslo': 'NO',
      'Europe/Copenhagen': 'DK',

      // Americas
      'America/New_York': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'America/Toronto': 'CA',
      'America/Mexico_City': 'MX',
      'America/Sao_Paulo': 'BR',
      'America/Buenos_Aires': 'AR',

      // Asia Pacific
      'Asia/Tokyo': 'JP',
      'Asia/Shanghai': 'CN',
      'Asia/Hong_Kong': 'HK',
      'Asia/Singapore': 'SG',
      'Asia/Seoul': 'KR',
      'Asia/Dubai': 'AE',
      'Asia/Mumbai': 'IN',
      'Australia/Sydney': 'AU',
      'Australia/Melbourne': 'AU',
      'Pacific/Auckland': 'NZ'
    };

    return timezoneMap[timezone] || null;
  }

  /**
   * Get current IP address (for cache invalidation)
   */
  async getCurrentIP() {
    try {
      // Use Cloudflare's trace endpoint to get IP
      const response = await fetch('https://cloudflare.com/cdn-cgi/trace');
      const text = await response.text();
      const ipMatch = text.match(/ip=([^\n]+)/);
      return ipMatch ? ipMatch[1] : null;
    } catch (e) {
      console.warn('Failed to get IP:', e);
      return null;
    }
  }

  /**
   * Detect region using IP geolocation (requires API)
   * Example using Cloudflare's free service
   */
  async detectByIP() {
    try {
      // Option 1: Cloudflare (free, no API key needed)
      console.log('🌐 Fetching IP geolocation from Cloudflare...');
      const response = await fetch('https://cloudflare.com/cdn-cgi/trace');
      const text = await response.text();
      const match = text.match(/loc=([A-Z]{2})/);
      const ipMatch = text.match(/ip=([^\n]+)/);

      if (match && match[1]) {
        const detectedRegion = match[1];
        const detectedIP = ipMatch ? ipMatch[1] : null;

        console.log('✅ IP geolocation detected:', {
          region: detectedRegion,
          ip: detectedIP,
          fullTrace: text.substring(0, 200) // First 200 chars for debugging
        });

        this.detectionMethods.ip = detectedRegion;
        this.detectionMethods.ipAddress = detectedIP;
        return {
          region: detectedRegion,
          ip: detectedIP
        };
      } else {
        console.warn('⚠️ IP geolocation: No region code found in response');
        console.log('Response text:', text.substring(0, 200));
      }
    } catch (e) {
      console.error('❌ IP detection failed:', e);
    }

    // Option 2: ipapi.co (free tier: 1000 requests/day)
    // Uncomment and add your API key if needed
    /*
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data.country_code) {
        this.detectionMethods.ip = data.country_code;
        this.detectionMethods.ipAddress = data.ip;
        return {
          region: data.country_code,
          ip: data.ip
        };
      }
    } catch (e) {
      console.warn('IP detection failed:', e);
    }
    */

    return null;
  }

  /**
   * Get detected region with confidence score
   */
  async detect() {
    const ipResult = await this.detectByIP();
    const results = {
      language: this.detectByLanguage(),
      timezone: this.detectByTimezone(),
      ip: ipResult ? ipResult.region : null,
      ipAddress: ipResult ? ipResult.ip : null
    };

    // Determine best region based on available data
    let detectedRegion = null;
    let confidence = 'low';

    // High confidence: IP geolocation
    if (results.ip) {
      detectedRegion = results.ip;
      confidence = 'high';
    }
    // Medium confidence: Timezone region hint
    else if (results.timezone && results.timezone.regionHint) {
      detectedRegion = results.timezone.regionHint;
      confidence = 'medium';
    }
    // Low confidence: Language preference
    else if (results.language) {
      // Map common languages to countries
      const langToCountry = {
        'de': 'DE',
        'en': 'US', // Default, but could be GB, CA, AU, etc.
        'es': 'ES',
        'fr': 'FR',
        'it': 'IT',
        'pt': 'BR',
        'pl': 'PL',
        'nl': 'NL',
        'sv': 'SE',
        'da': 'DK',
        'no': 'NO',
        'fi': 'FI'
      };
      detectedRegion = langToCountry[results.language] || results.language.toUpperCase();
      confidence = 'low';
    }

    this.detectedRegion = {
      region: detectedRegion,
      confidence: confidence,
      methods: this.detectionMethods,
      raw: results,
      ipAddress: results.ipAddress // Store IP for cache invalidation
    };

    return this.detectedRegion;
  }

  /**
   * Get region code (ISO 3166-1 alpha-2)
   */
  getRegion() {
    return this.detectedRegion?.region || null;
  }

  /**
   * Check if user is in a specific region
   */
  isRegion(regionCode) {
    return this.detectedRegion?.region === regionCode.toUpperCase();
  }

  /**
   * Store region in localStorage for future visits
   */
  saveToStorage() {
    // Don't save if caching is disabled
    if (this._disableCache) {
      console.log('🚫 Caching disabled - not saving to storage');
      return;
    }

    if (this.detectedRegion) {
      try {
        localStorage.setItem('user_region', JSON.stringify({
          region: this.detectedRegion.region,
          ipAddress: this.detectedRegion.ipAddress || null,
          timestamp: Date.now(),
          expires: Date.now() + (1 * 60 * 60 * 1000) // 1 hour (reduced from 24 hours)
        }));
      } catch (e) {
        console.warn('Failed to save region to storage:', e);
      }
    }
  }

  /**
   * Load region from localStorage if still valid
   * Checks if IP or region has changed and invalidates cache if it has
   */
  async loadFromStorage() {
    try {
      const stored = localStorage.getItem('user_region');
      if (stored) {
        const data = JSON.parse(stored);

        // Check if cache is still valid (not expired)
        if (data.expires > Date.now()) {
          // Get current IP and region for comparison
          const currentIP = await this.getCurrentIP();

          // Also do a quick region check to see if region changed
          // (This is faster than full detection, just checks IP geolocation)
          let currentRegion = null;
          try {
            const response = await fetch('https://cloudflare.com/cdn-cgi/trace');
            const text = await response.text();
            const match = text.match(/loc=([A-Z]{2})/);
            if (match && match[1]) {
              currentRegion = match[1];
            }
          } catch (e) {
            // Ignore errors in quick check
          }

          // Invalidate cache if IP changed
          if (currentIP && data.ipAddress && currentIP !== data.ipAddress) {
            console.log('🔄 IP changed from', data.ipAddress, 'to', currentIP, '- invalidating cache');
            localStorage.removeItem('user_region');
            return null;
          }

          // Invalidate cache if region changed (even if IP is same)
          if (currentRegion && data.region && currentRegion !== data.region) {
            console.log('🔄 Region changed from', data.region, 'to', currentRegion, '- invalidating cache');
            localStorage.removeItem('user_region');
            return null;
          }

          // Log cache hit with details
          console.log('✅ Using cached region:', {
            region: data.region,
            ip: data.ipAddress || 'unknown',
            currentIP: currentIP || 'unknown',
            currentRegion: currentRegion || 'unknown',
            expiresIn: Math.round((data.expires - Date.now()) / 1000 / 60) + ' minutes'
          });

          // Cache is valid and IP/region haven't changed
          this.detectedRegion = {
            region: data.region,
            confidence: 'cached',
            methods: { cached: true },
            ipAddress: data.ipAddress || null
          };
          return this.detectedRegion;
        } else {
          // Cache expired - remove it
          console.log('⏰ Cache expired - removing');
          localStorage.removeItem('user_region');
        }
      }
    } catch (e) {
      // Ignore errors
      console.warn('Failed to load region from storage:', e);
    }
    return null;
  }

  /**
   * Clear cached region data (useful for testing)
   */
  clearCache() {
    try {
      localStorage.removeItem('user_region');
      console.log('🗑️ Region cache cleared');
      return true;
    } catch (e) {
      console.warn('Failed to clear cache:', e);
      return false;
    }
  }
}

// Auto-detect on page load
if (typeof window !== 'undefined') {
  window.RegionDetection = RegionDetection;

  // Initialize and detect
  const regionDetector = new RegionDetection();

  // Expose clearCache method globally for testing/debugging
  window.clearRegionCache = function() {
    const cleared = regionDetector.clearCache();
    if (cleared) {
      console.log('💡 Cache cleared! Reload page or call window.forceRegionDetection() to detect fresh region.');
    }
    return cleared;
  };

  // Expose force detection method for testing
  window.forceRegionDetection = function() {
    regionDetector.clearCache();
    console.log('🔍 Forcing fresh region detection...');
    return regionDetector.detect().then(result => {
      window.userRegion = result;
      regionDetector.saveToStorage();
      console.log('📍 Region detected (forced fresh):', result);
      window.dispatchEvent(new CustomEvent('regionDetected', {
        detail: result
      }));
      return result;
    }).catch(error => {
      console.error('❌ Region detection failed:', error);
      throw error;
    });
  };

  // Check for URL parameters (useful for testing)
  const urlParams = new URLSearchParams(window.location.search);
  const clearCacheParam = urlParams.get('clearRegionCache');
  const disableCacheParam = urlParams.get('disableRegionCache');

  // If disableRegionCache=true, don't use cache at all (for testing)
  if (disableCacheParam === 'true') {
    regionDetector.clearCache();
    console.log('🚫 Region cache disabled via URL parameter. Fresh detection will be performed.');
    // Set a flag to prevent caching
    regionDetector._disableCache = true;
  }

  // If clearRegionCache=true, clear cache and force fresh detection
  if (clearCacheParam === 'true') {
    regionDetector.clearCache();
    console.log('🔄 Cache cleared via URL parameter. Performing fresh detection...');
    console.log('💡 Make sure VPN is fully connected before refreshing the page.');

    // Force fresh detection immediately
    regionDetector.detect().then(result => {
      window.userRegion = result;
      // Only save if caching is not disabled
      if (!regionDetector._disableCache) {
        regionDetector.saveToStorage();
      }
      console.log('📍 Region detected (fresh, cache cleared):', result);
      console.log('💡 If region is incorrect, wait a few seconds for VPN to fully connect, then refresh again.');

      window.dispatchEvent(new CustomEvent('regionDetected', {
        detail: result
      }));
    }).catch(error => {
      console.error('❌ Region detection failed:', error);
    });
  } else {
    // Normal flow: Try loading from cache first (async now to check IP and region)
    regionDetector.loadFromStorage().then(cached => {
      if (cached && !regionDetector._disableCache) {
        window.userRegion = cached;
        console.log('📍 Region detected (cached):', cached);

        // Dispatch event for other scripts
        window.dispatchEvent(new CustomEvent('regionDetected', {
          detail: cached
        }));
      } else {
        // Perform fresh detection
        console.log('🔍 Performing fresh region detection...');
        regionDetector.detect().then(result => {
          window.userRegion = result;
          // Only save if caching is not disabled
          if (!regionDetector._disableCache) {
            regionDetector.saveToStorage();
          }
          console.log('📍 Region detected (fresh):', result);

          // Dispatch custom event for other scripts to listen
          window.dispatchEvent(new CustomEvent('regionDetected', {
            detail: result
          }));
        }).catch(error => {
          console.error('❌ Region detection failed:', error);
        });
      }
    });
  }
}
